import { useQueries, useQueryClient } from 'react-query';

import { type Translations } from 'common/components/QueryError';
import { getFetcher } from 'src/core/api/fetcher';
import { useQuery } from 'src/core/api/hooks/useQuery';
import { useCompanyId } from 'src/core/modules/app/hooks/useCompanyId';

import { type RawSupplier, transformSupplier } from './supplierTransformer';
import { type SupplierDetails, type SupplierId } from '../../../models';

type SupplierData = {
  rawSupplier: RawSupplier;
  supplierDetails: SupplierDetails;
};

type Options = {
  isEnabled?: boolean;
};

export const useFetchSupplier = (
  supplierId: SupplierId | undefined,
  { isEnabled = true }: Options = {},
) => {
  return useQuery<SupplierData, RawSupplier>({
    key: ['suppliers', supplierId],
    request: {
      type: 'rest',
      target: 'companyAPI',
      endpoint: `/suppliers/${supplierId}`,
    },
    isEnabled: Boolean(supplierId) && isEnabled,
    reshapeData: reshaper,
  });
};

export const useFetchSuppliers = (
  supplierIds: SupplierId[],
  { isEnabled = true }: Options = {},
) => {
  const companyId = useCompanyId();

  return useQueries(
    supplierIds.map((supplierId) => {
      const fetcher = getFetcher<RawSupplier>({
        companyId,
        getRequest: () => ({
          type: 'rest',
          target: 'companyAPI',
          endpoint: `/suppliers/${supplierId}`,
          method: 'get',
        }),
      });

      return {
        queryKey: ['suppliers', supplierId],
        queryFn: fetcher,
        enabled: Boolean(supplierId) && isEnabled,
      };
    }),
  );
};

const reshaper = (rawSupplier: RawSupplier): SupplierData => ({
  rawSupplier,
  supplierDetails: transformSupplier(rawSupplier),
});

export const useInvalidateSupplierQuery = () => {
  const queryClient = useQueryClient();

  return (supplierId: SupplierId): void => {
    queryClient.invalidateQueries(['suppliers', supplierId]);
  };
};

export const useSetSupplierQueryData = () => {
  const queryClient = useQueryClient();

  return (supplier: RawSupplier): void => {
    queryClient.setQueryData(['suppliers', supplier.id], supplier);
  };
};

export const supplierErrorTranslations: Translations<unknown> = {
  requestError: () => {
    return 'suppliers.fetchError';
  },
  serverError: 'suppliers.fetchError',
};
